import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackEvent } from '../../analytics';
import { parseReturnToPath } from '../../navigation/authRedirect';
import styles from './Login.module.css';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPassword, resendVerificationEmail } = useAuth();
  const [accountIdentifier, setAccountIdentifier] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountError, setAccountError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showVerifiedBanner = urlParams.get('verified') === '1';
  const showCheckEmailBanner = urlParams.get('checkEmail') === '1';
  const returnTo = parseReturnToPath((location.state as { returnTo?: unknown } | null)?.returnTo) ?? '/';

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  useEffect(() => {
    if (showVerifiedBanner) {
      trackEvent('email_verified', { entry: 'email_verification_link' });
    }
  }, [showVerifiedBanner]);

  useEffect(() => {
    if (showCheckEmailBanner && !showVerifiedBanner) {
      trackEvent('email_verification_prompt_seen', { entry: 'post_signup_redirect' });
    }
  }, [showCheckEmailBanner, showVerifiedBanner]);

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recaptchaSiteKey && !recaptchaToken) {
      setAccountError('Please complete the CAPTCHA.');
      return;
    }
    setAccountError('');
    setLoading(true);
    try {
      const result = await loginWithPassword(
        accountIdentifier.trim(),
        accountPassword,
        recaptchaSiteKey ? recaptchaToken ?? undefined : undefined
      );
      if (result.ok) {
        trackEvent('login_success', { entry: 'password_login' });
        navigate(returnTo, { replace: true });
      }
      else setAccountError(result.error ?? 'Invalid username/email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendMessage('');
    setResendError('');
    const trimmed = resendEmail.trim();
    if (!trimmed) {
      setResendError('Please enter your email.');
      return;
    }
    setResendLoading(true);
    try {
      const result = await resendVerificationEmail(trimmed);
      if (result.success) {
        setResendMessage(result.message);
      } else {
        setResendError(result.message);
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1>Sign in</h1>
        {showVerifiedBanner && (
          <p className={styles.bannerSuccess}>Your email has been verified. You can now sign in.</p>
        )}
        {showCheckEmailBanner && !showVerifiedBanner && (
          <p className={styles.bannerInfo}>Account created. Check your email to verify before signing in.</p>
        )}
        <p className={styles.subtitle}>Sign in with your account</p>

        <form onSubmit={handleAccountSubmit}>
          <label className={styles.label}>Username or email</label>
          <input
            type="text"
            value={accountIdentifier}
            onChange={(e) => setAccountIdentifier(e.target.value)}
            placeholder="Your username or email"
            className={styles.input}
            autoComplete="username"
          />
          <label className={styles.label}>Password</label>
          <input
            type="password"
            value={accountPassword}
            onChange={(e) => setAccountPassword(e.target.value)}
            placeholder="Your password"
            className={styles.input}
          />
          {recaptchaSiteKey && (
            <div className={styles.captchaRow}>
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                onChange={(token: string | null) => {
                  setRecaptchaToken(token);
                  if (token && accountError === 'Please complete the CAPTCHA.') {
                    setAccountError('');
                  }
                }}
              />
            </div>
          )}
          {accountError && <p className={styles.error}>{accountError}</p>}
          <button type="submit" className={styles.submitSecondary} disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className={styles.resendBlock}>
          <p className={styles.resendTitle}>Didn’t get the verification email?</p>
          <form onSubmit={handleResendVerification}>
            <input
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="Enter your email"
              className={styles.input}
            />
            {resendError && <p className={styles.error}>{resendError}</p>}
            {resendMessage && <p className={styles.resendMessage}>{resendMessage}</p>}
            <button type="submit" className={styles.resendButton} disabled={resendLoading}>
              {resendLoading ? 'Sending...' : 'Resend verification email'}
            </button>
          </form>
        </div>

        <p className={styles.footer}>
          Don’t have an account? <Link to="/create-account">Create account</Link>
        </p>
      </div>
    </div>
  );
}
